import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import brand1 from "assets/img/brand/hd/pantaloons.webp";
import pantaloonsWordmark from "assets/img/brand/hd/pantaloons-wordmark.webp";
import brand2 from "assets/img/brand/hd/pexpo.webp";
import brand3 from "assets/img/brand/hd/realwear.webp";
import brand4 from "assets/img/brand/hd/saudicast.webp";
import brand5 from "assets/img/brand/hd/rcb.webp";
import brand6 from "assets/img/brand/hd/partner-wave.webp";
import brand7 from "assets/img/brand/hd/fastor-7.webp";
import brand8 from "assets/img/brand/hd/zyn.webp";
import numeroUno from "assets/img/brand/numero-uno.png";
import adnoc from "assets/img/brand/adnoc.png";
import saudiAramco from "assets/img/brand/saudi-aramco.png";
import lilly from "assets/img/brand/lilly.png";
import iqos from "assets/img/brand/iqos.png";
import gamingPartner from "assets/img/brand/gaming-partner.png";
import desoniqStudio from "assets/img/brand/desoniq-studio.png";
import cyberhub from "assets/img/brand/145-cyberhub.png";

// Shared with the Hall of Fame page (`name`/`sector` are its frame plaques;
// `onDark` = logo needs a dark mat there; `mat` = the logo's own baked-in background,
// used as the frame canvas colour so the image edge disappears; `frame` = a different
// image just for the Hall of Fame frame).
export const brandLogos = [
  { src: brand1, alt: "Pantaloons logo", name: "Pantaloons", sector: "Fashion retail", mat: "#00A6BB", size: [900, 229], bounds: "0 0 900 229", frame: { src: pantaloonsWordmark, size: [886, 204], bounds: "0 0 886 204" } },
  { src: brand2, alt: "Pexpo logo", name: "Pexpo", size: [625, 217], bounds: "0 0 625 217", onDark: true },
  { src: brand3, alt: "RealWear logo", name: "RealWear", sector: "Wearable technology", size: [900, 215], bounds: "0 0 900 215" },
  { src: brand4, alt: "Saudicast logo", name: "Saudicast", size: [300, 105], bounds: "0 0 300 105" },
  { src: brand5, alt: "Royal Challengers Bengaluru logo", name: "Royal Challengers Bengaluru", sector: "Sports", size: [493, 700], bounds: "0 0 493 700", onDark: true },
  { src: brand6, alt: "Partner brand logo", name: "Partner Brand", size: [372, 215], bounds: "0 0 372 215", onDark: true },
  { src: brand7, alt: "Fastor 7 logo", name: "Fastor 7", sector: "Product, design & technology", size: [839, 163], bounds: "0 0 839 163" },
  { src: brand8, alt: "ZYN logo", name: "ZYN", sector: "Consumer goods", size: [900, 373], bounds: "0 0 900 373" },
  { src: numeroUno, alt: "Numero Uno logo", name: "Numero Uno", sector: "Fashion", mat: "#FFFFFF", size: [233, 233], bounds: "27 23 180 200" },
  { src: adnoc, alt: "ADNOC logo", name: "ADNOC", sector: "Energy", mat: "#FFFFFF", size: [554, 554], bounds: "135 47 286 462" },
  { src: saudiAramco, alt: "Saudi Aramco logo", name: "Saudi Aramco", sector: "Energy", size: [350, 350], bounds: "37 137 276 78" },
  { src: lilly, alt: "Lilly - A Medicine Company logo", name: "Lilly", sector: "Healthcare", mat: "#FFFFFF", size: [1024, 630], bounds: "150 151 724 331" },
  { src: iqos, alt: "IQOS logo", name: "IQOS", sector: "Consumer technology", mat: "#FFFFFF", size: [673, 553], bounds: "56 8 560 532" },
  { src: gamingPartner, alt: "Gaming partner logo", name: "Gaming Partner", sector: "Gaming", size: [1540, 1624], bounds: "0 0 1540 1624", dark: true, onDark: true },
  { src: desoniqStudio, alt: "Desoniq Studio logo", name: "Desoniq Studio", sector: "Creative studio", size: [1600, 491], bounds: "28 16 1548 448", dark: true, onDark: true },
  { src: cyberhub, alt: "145 Cyberhub cafe and bar logo", name: "145 Cyberhub", sector: "Café & bar", mat: "#FFFFFF", size: [447, 447], bounds: "68 68 336 292" },
];

const BrandsMarquee = ({ logos, reverse = false }) => {
  const marqueeRef = useRef(null);
  const trackRef = useRef(null);
  const dragStartRef = useRef({ x: 0, offset: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const marqueeLogos = [0, 1, 2].flatMap(() => logos);

  const normalizeOffset = (offset) => {
    const track = trackRef.current;
    const cycleWidth = track ? track.scrollWidth / 3 : 0;

    if (!cycleWidth) return offset;

    const normalized = ((offset % cycleWidth) + cycleWidth) % cycleWidth;
    return normalized === 0 ? 0 : normalized - cycleWidth;
  };

  const handlePointerDown = (event) => {
    const marquee = marqueeRef.current;
    if (!marquee) return;

    setIsDragging(true);
    dragStartRef.current = {
      x: event.clientX,
      offset: dragOffset,
    };
    marquee.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const marquee = marqueeRef.current;
    if (!isDragging || !marquee) return;

    event.preventDefault();
    const dragDistance = event.clientX - dragStartRef.current.x;
    setDragOffset(normalizeOffset(dragStartRef.current.offset + dragDistance));
  };

  const stopDragging = (event) => {
    const marquee = marqueeRef.current;
    setIsDragging(false);

    if (marquee?.hasPointerCapture?.(event.pointerId)) {
      marquee.releasePointerCapture(event.pointerId);
    }
  };

  return (
      <div
        ref={marqueeRef}
        className={`trusted-brands__marquee${reverse ? " trusted-brands__marquee--reverse" : ""}${isDragging ? " is-dragging" : ""}`}
        aria-label="Brands we have worked with"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onPointerLeave={isDragging ? stopDragging : undefined}
      >
        <div
          ref={trackRef}
          className="trusted-brands__track"
          style={{ "--trusted-brands-drag-offset": `${dragOffset}px` }}
        >
          {marqueeLogos.map((logo, index) => (
            <div
              className={`trusted-brands__item${logo.dark ? " trusted-brands__item--dark" : ""}`}
              key={`${logo.alt}-${index}`}
              aria-hidden={index >= logos.length ? true : undefined}
            >
              <svg
                viewBox={logo.bounds}
                role="img"
                aria-label={logo.alt}
                preserveAspectRatio="xMidYMid meet"
              >
                <image href={logo.src} width={logo.size[0]} height={logo.size[1]} />
              </svg>
            </div>
          ))}
        </div>
      </div>
  );
};

const HomeTrustedBrands = () => (
  <section className="trusted-brands__area">
    <div className="container">
      <div className="trusted-brands__heading text-center">
        <span>Trusted Collaborations</span>
        <h3>Brands We Have Worked With</h3>
      </div>
    </div>
    <div className="trusted-brands__desktop-rows">
      <BrandsMarquee logos={brandLogos.filter((_, index) => index % 2 === 0)} />
      <BrandsMarquee logos={brandLogos.filter((_, index) => index % 2 === 1)} reverse />
    </div>
    <div className="trusted-brands__mobile-row">
      <BrandsMarquee logos={brandLogos} />
    </div>
    <div className="trusted-brands__action">
      <Link to="/hall-of-fame" state={{ curtain: true }} className="tp-btnr bg-danger tp-btn-shine-effect">
        Enter the Hall of Fame <i className="fa-regular fa-arrow-right"></i>
      </Link>
    </div>
  </section>
);

export default HomeTrustedBrands;
